import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, Download, CheckCircle, FileText, 
  AlertCircle, Shield, Users, Pen, Scale
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_estate-planner-16/artifacts/7xqxcajt_LOGO.jpg';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WillReviewPage = () => {
  const { willId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [will, setWill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchWill();
  }, [willId]);

  const fetchWill = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wills/${willId}`, getAuthHeaders());
      setWill(response.data);
    } catch (error) {
      console.error('Error fetching will:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API_URL}/api/wills/${willId}/complete`, {}, getAuthHeaders());
      toast.success('Will completed successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      console.error('Error completing will:', error);
      toast.error('Failed to complete will');
    } finally {
      setCompleting(false);
    }
  };

  const handleDownloadWill = () => {
    window.open(`${API_URL}/api/wills/${willId}/download/will`, '_blank');
  };

  const handleDownloadQuestionnaire = () => {
    window.open(`${API_URL}/api/wills/${willId}/download/questionnaire`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="spinner"></div>
      </div>
    );
  }

  const q = will?.questionnaire || {};
  const pi = q.personal_info || {};
  const si = q.spouse_info || {};
  const ci = q.children_info || {};
  const di = q.distribution_info || {};
  const ei = q.executors_info || {};

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="will-review-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={`/questionnaire/${willId}`} className="flex items-center gap-2 text-[#475569] hover:text-[#0A192F]">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Questionnaire</span>
            </Link>
            <img src={LOGO_URL} alt="Clevermoney" className="h-8 w-auto" />
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl text-[#0A192F] mb-2">Review Your Will</h1>
          <p className="text-[#475569]">
            Please review the information below before completing your will.
          </p>
        </div>

        {/* Will Type Card */}
        <Card className="mb-6 border-[#C5A059] border-2" data-testid="will-type-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-xl text-[#0A192F] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C5A059]" />
              Will Type Selected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium text-[#0A192F]">{will?.will_type_name}</p>
            <p className="text-sm text-[#475569] mt-1">
              This template has been selected based on your personal circumstances.
            </p>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Personal Details */}
          <Card data-testid="personal-summary">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-4 h-4 text-[#C5A059]" />
                Personal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-[#475569]">Name:</span>
                <span className="font-medium">{pi.full_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#475569]">Address:</span>
                <span className="font-medium text-right">{pi.home_address || '-'}, {pi.county || ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#475569]">Marital Status:</span>
                <span className="font-medium">{si.is_married ? 'Married' : 'Single'}</span>
              </div>
              {si.is_married && (
                <div className="flex justify-between">
                  <span className="text-[#475569]">Spouse:</span>
                  <span className="font-medium">{si.spouse_full_name || '-'}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Children */}
          <Card data-testid="children-summary">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-4 h-4 text-[#C5A059]" />
                Children
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-[#475569]">Has Children:</span>
                <span className="font-medium">{ci.has_children ? 'Yes' : 'No'}</span>
              </div>
              {ci.has_children && ci.children?.length > 0 && (
                <div>
                  <span className="text-[#475569]">Children:</span>
                  <ul className="mt-1 space-y-1">
                    {ci.children.map((child, i) => (
                      <li key={i} className="font-medium">
                        {child.full_name} {child.is_minor && <span className="text-xs text-[#C5A059]">(Minor)</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Beneficiaries */}
          <Card data-testid="beneficiaries-summary">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Scale className="w-4 h-4 text-[#C5A059]" />
                Residuary Beneficiaries
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {di.residuary_beneficiaries?.length > 0 ? (
                <ul className="space-y-2">
                  {di.residuary_beneficiaries.map((ben, i) => (
                    <li key={i} className="flex justify-between">
                      <span>{ben.full_name}</span>
                      <span className="text-[#475569]">{ben.share_percentage}%</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[#475569]">No beneficiaries specified</p>
              )}
            </CardContent>
          </Card>

          {/* Executors */}
          <Card data-testid="executors-summary">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Pen className="w-4 h-4 text-[#C5A059]" />
                Executors
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {ei.executors?.length > 0 ? (
                <ul className="space-y-2">
                  {ei.executors.map((exec, i) => (
                    <li key={i} className="flex justify-between">
                      <span>{exec.full_name}</span>
                      <span className="text-[#475569] capitalize">{exec.role?.replace('_', ' ')}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[#475569]">No executors specified</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Important Instructions */}
        <Card className="mb-8 bg-[#0A192F] text-white" data-testid="instructions-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#C5A059]" />
              Important: Making Your Will Valid
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              After downloading your will, you must follow these steps to make it legally valid:
            </p>
            <ol className="list-decimal list-inside space-y-3 text-gray-300">
              <li>
                <strong className="text-white">Print</strong> the will document
              </li>
              <li>
                <strong className="text-white">Sign</strong> the will at the bottom in the presence of <strong className="text-white">two witnesses</strong> who are both present at the same time
              </li>
              <li>
                <strong className="text-white">Witnesses must sign</strong> the will in your presence and in each other's presence
              </li>
              <li>
                Witnesses should <strong className="text-white">not be beneficiaries</strong> or spouses of beneficiaries
              </li>
              <li>
                <strong className="text-white">Store safely</strong> - keep your will in a secure place and inform your executor
              </li>
            </ol>
            <Alert className="bg-[#C5A059]/20 border-[#C5A059]">
              <AlertCircle className="h-4 w-4 text-[#C5A059]" />
              <AlertDescription className="text-white">
                <strong>Recommendation:</strong> If you have complex circumstances, overseas assets, 
                or any uncertainty, we recommend consulting a solicitor to review your will.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Download Section */}
        <Card className="mb-8" data-testid="download-section">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Download Your Documents</CardTitle>
            <CardDescription>Download both documents for your records</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleDownloadWill}
                className="flex-1 bg-[#C5A059] hover:bg-[#997B40]"
                data-testid="download-will-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Will (PDF)
              </Button>
              <Button 
                onClick={handleDownloadQuestionnaire}
                variant="outline"
                className="flex-1 border-[#0A192F] text-[#0A192F]"
                data-testid="download-questionnaire-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Questionnaire (PDF)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Complete Button */}
        {!will?.is_complete && (
          <div className="text-center">
            <Button 
              onClick={handleComplete}
              disabled={completing}
              size="lg"
              className="bg-[#10B981] hover:bg-[#059669] text-lg px-12"
              data-testid="complete-will-btn"
            >
              {completing ? (
                'Completing...'
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Mark Will as Complete
                </>
              )}
            </Button>
            <p className="text-sm text-[#475569] mt-4">
              By clicking complete, you confirm that you have reviewed your will and understand 
              the witnessing requirements.
            </p>
          </div>
        )}

        {will?.is_complete && (
          <Alert className="bg-[#10B981]/10 border-[#10B981]">
            <CheckCircle className="h-4 w-4 text-[#10B981]" />
            <AlertDescription className="text-[#0A192F]">
              <strong>Will Completed!</strong> Your will has been marked as complete. 
              You can download it anytime from your dashboard.
            </AlertDescription>
          </Alert>
        )}

        {/* Footer Note */}
        <div className="mt-12 p-6 bg-white rounded-lg border border-[#E2E8F0]">
          <p className="text-xs text-[#475569] font-legal leading-relaxed">
            <strong>Legal Notice:</strong> The provision of this product or service does not 
            require licensing, authorization, or registration with the Central Bank of Ireland 
            and, as a result, it is not covered by Central Bank consumer protection requirements 
            or a statutory scheme. Charterhouse Private Clients Ltd trading as Clevermoney is 
            regulated by the Central Bank of Ireland.
          </p>
        </div>
      </main>
    </div>
  );
};

export default WillReviewPage;
